import { useMemo } from 'react';
import { Box } from '@mui/material';
import { PolicyGetResponse } from '@lio/api-types';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { useSearchReviewAtRenewal } from '@services/policy';
import { Dialog } from '@components/dialog';
import { Table } from '@components/table/Table';
import { REVIEW_REASON_LABELS } from '@constants/reviewReason';
import { getDateFormatter, LongDateFormatWithoutSeconds } from '@utils/date';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import type { PolicyReviewAtRenewalItem, PolicyReviewAtRenewalGetRequestQuery } from '@lio/api-types';
import { ClockFormat, ReviewReason } from '@lio/rule-engine';
import { format } from 'date-fns';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';

export interface ReviewAtRenewalActivityModalProps {
  open: boolean;
  policy: PolicyGetResponse;
  onClose: () => void;
}

type TReviewAtRenewalTable = {
  items: PolicyReviewAtRenewalItem[];
  params: PolicyReviewAtRenewalGetRequestQuery & { policyId: string };
};

const getReviewAtRenewalColumns = (clockFormat: ClockFormat): TableGridBaseColDef<PolicyReviewAtRenewalItem>[] => [
  {
    field: 'reviewReasonLabel',
    headerName: 'Reason for Review',
    minWidth: 150,
    flex: 1,
    sortable: false,
    valueGetter: (value, row) => {
      return REVIEW_REASON_LABELS[row.reviewReason as ReviewReason] || value;
    },
  },
  {
    field: 'policyTermCount',
    headerName: 'Policy Term',
    width: 100,
    sortable: false,
    valueGetter: (_, row) => {
      return row.policyTerm?.termCount;
    },
  },
  {
    field: 'createdByName',
    headerName: 'Created By',
    width: 150,
    sortable: false,
    valueGetter: (_, row) => {
      if (!row.createdByUser) {
        return null;
      }
      return `${row.createdByUser?.firstName || ''} ${row.createdByUser?.lastName || ''}`.trim();
    },
  },
  {
    field: 'createdAt',
    headerName: 'Created Date',
    width: 130,
    sortable: false,
    valueGetter: (value) => {
      return format(new Date(value), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds));
    },
  },
  {
    field: 'reviewedByName',
    headerName: 'Reviewed By',
    width: 150,
    sortable: false,
    valueGetter: (_, row) => {
      if (!row.reviewedByUser) {
        return null;
      }
      return `${row.reviewedByUser?.firstName || ''} ${row.reviewedByUser?.lastName || ''}`.trim();
    },
  },
  {
    field: 'reviewedAt',
    headerName: 'Reviewed Date',
    width: 130,
    sortable: false,
    valueGetter: (value) => {
      if (!value) {
        return null;
      }
      return format(new Date(value), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds));
    },
  },
  {
    field: 'deletedByName',
    headerName: 'Removed By',
    width: 150,
    sortable: false,
    valueGetter: (_, row) => {
      if (!row.deletedByUser) {
        return null;
      }
      return `${row.deletedByUser?.firstName || ''} ${row.deletedByUser?.lastName || ''}`.trim();
    },
  },
  {
    field: 'deletedAt',
    headerName: 'Removed Date',
    width: 130,
    sortable: false,
    valueGetter: (value) => {
      if (!value) {
        return null;
      }
      return format(new Date(value), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds));
    },
  },
];

export const ReviewAtRenewalActivityModal = ({ open, policy, onClose }: ReviewAtRenewalActivityModalProps) => {
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const policyNumber = getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber);

  const columns = useMemo(() => {
    return getReviewAtRenewalColumns(clockFormat);
  }, [clockFormat]);

  const useSearchReviewAtRenewalWrapper = (params: PolicyReviewAtRenewalGetRequestQuery) => {
    return useSearchReviewAtRenewal({
      ...params,
      policyId: policy.id,
    });
  };

  return (
    <Dialog
      open={open}
      title={`Review at Renewal Reasons for ${policyNumber}`}
      secondaryBtnText='Exit'
      secondaryBtnAction={onClose}
      handleCloseDialog={onClose}
      primaryBtnText={undefined}
      size='extraLarge'
      fullWidth
    >
      <Box sx={{ padding: '16px 24px', minHeight: '400px' }}>
        <Table<TReviewAtRenewalTable>
          ariaLabel='review-at-renewal-activity-table'
          onLoadData={useSearchReviewAtRenewalWrapper}
          columns={columns}
          paginationConfig={{ currentPage: 0, rowsPerPage: 10, pageOffset: 0 }}
          noResultsConfig={{
            disableNoMatchFound: false,
            noResultsMessage: 'No review at renewal records found for this policy.',
          }}
        />
      </Box>
    </Dialog>
  );
};
